import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  Users, UserCheck, UserMinus, Wrench, FileDown, RefreshCw, Search,
} from 'lucide-react';
import { getUsageReport } from '../api/reports';
import { getTools } from '../api/tools';

const DATE_RANGES = [
  { value: 'this_month',    label: 'This Month' },
  { value: 'last_month',    label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'this_year',     label: 'This Year' },
  { value: 'last_year',     label: 'Last Year' },
  { value: 'custom',        label: 'Custom Range' },
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const SummaryCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="card p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const exportCSV = (data, selectedToolName) => {
  if (!data) return;
  const rows = [];
  rows.push(['AI Tool Management — Usage Report']);
  rows.push([`Period: ${fmtDate(data.date_from)} – ${fmtDate(data.date_to)}`]);
  if (selectedToolName) rows.push([`Tool: ${selectedToolName}`]);
  rows.push([]);

  rows.push(['SUMMARY']);
  rows.push(['Active Users', 'Removed / Migrated Users']);
  rows.push([data.summary.active_count, data.summary.removed_count]);
  rows.push([]);

  rows.push(['ACTIVE EMPLOYEES']);
  rows.push(['Name', 'Email', 'Department', 'AI Tool', 'Start Date', 'End Date', 'Tool ID', 'Monthly Cost', 'Paid By']);
  data.active_users.forEach((u) =>
    rows.push([u.name, u.email, u.department, u.tool_name,
      fmtDate(u.start_date), fmtDate(u.end_date), u.tool_identifier || '',
      u.monthly_cost || '', u.paid_by || 'Bacancy'])
  );
  rows.push([]);

  rows.push(['REMOVED / MIGRATED EMPLOYEES']);
  rows.push(['Name', 'Email', 'Department', 'Tool', 'Start Date', 'End Date', 'Status']);
  data.removed_users.forEach((u) =>
    rows.push([u.name, u.email, u.department, u.tool_name,
      fmtDate(u.start_date), fmtDate(u.end_date), u.status])
  );

  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `usage_report_${data.date_from}_${data.date_to}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const EmployeeRow = ({ user, showStatus = false }) => (
  <tr className="hover:bg-gray-50 transition-colors">
    <td className="px-5 py-3.5">
      <div>
        <p className="font-medium text-gray-900 text-sm">{user.name}</p>
        <p className="text-xs text-gray-400">{user.email}</p>
      </div>
    </td>
    <td className="px-5 py-3.5">
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
        {user.department}
      </span>
    </td>
    <td className="px-5 py-3.5">
      <div className="flex items-center gap-1.5">
        <Wrench className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-700">{user.tool_name}</span>
      </div>
      {user.tool_identifier && (
        <p className="text-xs text-gray-400 mt-0.5 ml-5">ID: {user.tool_identifier}</p>
      )}
    </td>
    <td className="px-5 py-3.5 text-xs text-gray-500 whitespace-nowrap">
      {fmtDate(user.start_date)} → {fmtDate(user.end_date)}
    </td>
    {!showStatus && (
      <>
        <td className="px-5 py-3.5 text-sm font-medium text-gray-700">
          {fmt(user.monthly_cost)}
        </td>
        <td className="px-5 py-3.5">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.paid_by === 'Client' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
          }`}>
            {user.paid_by || 'Bacancy'}
          </span>
        </td>
      </>
    )}
    {showStatus && (
      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          user.status === 'migrated'
            ? 'bg-blue-50 text-blue-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {user.status}
        </span>
      </td>
    )}
  </tr>
);

const UsageReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tools, setTools] = useState([]);
  const [range, setRange] = useState('this_month');
  const [toolId, setToolId] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [removedSearch, setRemovedSearch] = useState('');

  useEffect(() => {
    getTools()
      .then((res) => setTools(res.data.filter((t) => t.is_active)))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    if (range === 'custom' && (!customFrom || !customTo)) {
      toast.error('Please select a custom date range.');
      return;
    }
    setLoading(true);
    setActiveSearch('');
    setRemovedSearch('');
    try {
      const params = { range, tool_id: toolId || undefined };
      if (range === 'custom') { params.from = customFrom; params.to = customTo; }
      const res = await getUsageReport(params);
      setData(res.data);
    } catch {
      toast.error('Failed to load usage report.');
    } finally {
      setLoading(false);
    }
  }, [range, toolId, customFrom, customTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const selectedToolName = tools.find((t) => t.id === Number(toolId))?.name;

  const filteredActive = data?.active_users?.filter((u) =>
    !activeSearch ||
    u.name.toLowerCase().includes(activeSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(activeSearch.toLowerCase()) ||
    u.department.toLowerCase().includes(activeSearch.toLowerCase())
  ) || [];

  const filteredRemoved = data?.removed_users?.filter((u) =>
    !removedSearch ||
    u.name.toLowerCase().includes(removedSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(removedSearch.toLowerCase()) ||
    u.department.toLowerCase().includes(removedSearch.toLowerCase())
  ) || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Report</h1>
          <p className="text-sm text-gray-500 mt-1">Track active and removed/migrated employees per AI tool.</p>
        </div>
        <button
          onClick={() => exportCSV(data, selectedToolName)}
          disabled={!data || loading}
          className="btn-secondary"
        >
          <FileDown className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">Date Range</label>
            <select className="input w-44" value={range} onChange={(e) => setRange(e.target.value)}>
              {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {range === 'custom' && (
            <>
              <div>
                <label className="label">From</label>
                <input className="input w-36" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              </div>
              <div>
                <label className="label">To</label>
                <input className="input w-36" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </div>
            </>
          )}

          <div>
            <label className="label">AI Tool</label>
            <select className="input w-48" value={toolId} onChange={(e) => setToolId(e.target.value)}>
              <option value="">All Tools</option>
              {tools.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <button onClick={fetchReport} disabled={loading} className="btn-primary h-[38px]">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Apply'}
          </button>
        </div>

        {data && (
          <p className="text-xs text-gray-400 mt-3">
            Period:{' '}
            <span className="font-medium text-gray-600">{fmtDate(data.date_from)}</span>
            {' '}–{' '}
            <span className="font-medium text-gray-600">{fmtDate(data.date_to)}</span>
            {selectedToolName && (
              <span className="ml-2 text-gray-500">· Tool: <span className="font-medium">{selectedToolName}</span></span>
            )}
          </p>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <SummaryCard
              icon={Users}
              label="Total in Period"
              value={Number(data.summary.active_count) + Number(data.summary.removed_count)}
              color="bg-primary-50 text-primary-600"
              sub="Active + removed"
            />
            <SummaryCard
              icon={UserCheck}
              label="Active Users"
              value={data.summary.active_count}
              color="bg-green-50 text-green-600"
              sub="Currently using the tool"
            />
            <SummaryCard
              icon={UserMinus}
              label="Removed / Migrated"
              value={data.summary.removed_count}
              color="bg-red-50 text-red-500"
              sub="Left or moved to another tool"
            />
          </div>

          {/* Active Users Table */}
          <div className="card mb-6">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-green-500" />
                <h2 className="text-sm font-semibold text-gray-700">
                  Active Employees
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                    {filteredActive.length}
                  </span>
                </h2>
              </div>
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  className="input pl-8 text-xs py-1.5"
                  placeholder="Search..."
                  value={activeSearch}
                  onChange={(e) => setActiveSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Employee</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Department</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">AI Tool</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Allocation Period</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Monthly Cost</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Paid By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredActive.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">
                        {activeSearch ? `No results for "${activeSearch}"` : 'No active employees for the selected period.'}
                      </td>
                    </tr>
                  ) : (
                    filteredActive.map((u, i) => <EmployeeRow key={`a-${i}`} user={u} />)
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Removed / Migrated Table */}
          <div className="card">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <UserMinus className="w-4 h-4 text-red-400" />
                <h2 className="text-sm font-semibold text-gray-700">
                  Removed / Migrated Employees
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                    {filteredRemoved.length}
                  </span>
                </h2>
              </div>
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  className="input pl-8 text-xs py-1.5"
                  placeholder="Search..."
                  value={removedSearch}
                  onChange={(e) => setRemovedSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Employee</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Department</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Tool Used</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Period</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredRemoved.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-400">
                        {removedSearch ? `No results for "${removedSearch}"` : 'No removed or migrated employees for the selected period.'}
                      </td>
                    </tr>
                  ) : (
                    filteredRemoved.map((u, i) => <EmployeeRow key={`r-${i}`} user={u} showStatus />)
                  )}
                </tbody>
              </table>
            </div>
            {filteredRemoved.length > 0 && (
              <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
                <span className="font-medium text-blue-600">migrated</span> = moved to a different AI tool ·{' '}
                <span className="font-medium text-gray-500">expired</span> = allocation period ended
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UsageReport;
